/** NIRA's records, as oio_service `nira/schemas.py` sends them. */

export type NiraLane = "for" | "against" | "dont_cite" | "check"
export type NiraSide = "revenue" | "taxpayer" | "mixed" | "neutral" | "unclear" | "none"
export type NiraFitMark = "yes" | "no" | "unclear"
export type NiraAction = "follow" | "distinguish" | "not_follow" | "cite_with_care"
export type NiraMode = "question" | "scenario" | "facts" | "document" | "follow_up"

export interface NiraContext {
  state_code: string
  administration: "central" | "state"
  as_of: string | null
  side: "revenue" | "taxpayer"
  forums: string[]
  kinds: string[]
  include_pre_gst: boolean
}

export interface NiraOverride {
  action: string
  reason: string
}

export interface NiraAnnotations {
  order: string[]
  overrides: Record<string, NiraOverride>
  dismissed: string[]
}

export interface NiraPassage {
  chunk_id: string
  para: string
  page: number | null
  text: string
  role: string
  matched: boolean
}

export interface NiraStatusEvent {
  kind: string
  label: string
  date: string
  note: string
  source: string
  verified_on: string
  confidence: string
  adverse: boolean
  caution: boolean
}

export interface NiraFitElement {
  element: string
  mark: NiraFitMark
  note: string
}

export interface NiraConnection {
  relation: string
  label: string
  doc_id: string
  date: string
  note: string
  source: string
}

export interface NiraAuthority {
  ref: string
  doc_id: string
  kind: string
  title: string
  short_name: string
  petitioner: string
  respondent: string
  court: string
  forum: string
  bench_strength: number | null
  coram: string[]
  decision_date: string
  case_number: string
  citations: string[]
  source_url: string
  quality_grade: string
  scanned: boolean
  regime: string
  analogue: string
  nira_binding: string
  binding_level: string
  binding_label: string
  binding_reason: string
  binds_officer: boolean
  disposal: string
  no_merits: string
  outcome_raw: string
  won_by: NiraSide
  won_by_basis: string
  status: NiraStatusEvent[]
  status_checked: boolean
  passages: NiraPassage[]
  connections: NiraConnection[]
  relevance: "on_point" | "related" | "off_point"
  helps: NiraSide
  holding: string
  holding_para: string
  quote: string
  quote_para: string
  quote_verified: boolean
  fit: NiraFitElement[]
  action: NiraAction
  action_reason: string
  read_by_engine: boolean
  lane: NiraLane
  lane_reason: string
  must_follow: boolean
  why_here: string
  matched_queries: number[]
  rank: number
}

export interface NiraIssue {
  text: string
  statute_refs: string[]
}

export interface NiraBrief {
  intent: "research" | "refine" | "discuss"
  mode: NiraMode
  question: string
  facts_summary: string
  issues: NiraIssue[]
  side: "revenue" | "taxpayer"
  state_code: string
  state_name: string
  administration: "central" | "state"
  as_of: string | null
  jurisdiction_only: boolean
  refers_to: string[]
  phrasings: string[]
  /** Authorities the officer's message names, looked up by name. */
  cited?: string[]
  names_redacted: number
  assumptions: string[]
}

export interface NiraSearchStat {
  query: string
  issue: number
  scope: "prefer" | "only" | "named"
  results: number
  candidates_total: number
  cases_before_limit: number
  echoes_collapsed: number
  elapsed_ms: number
  lanes: Array<{ name: string; candidates: number; ran: boolean }>
  notes: string[]
  error: string
}

export interface NiraMemoSentence {
  text: string
  refs: string[]
}

export interface NiraAnswer {
  markdown: string
  sentences: NiraMemoSentence[]
  follow_ups: string[]
  by_engine: boolean
}

export interface NiraTurn {
  brief: NiraBrief
  answer: NiraAnswer
  authorities: NiraAuthority[]
  set_aside: NiraAuthority[]
  law: NiraAuthority[]
  searches: NiraSearchStat[]
  jurisdiction_note: string
  lane_counts: Partial<Record<NiraLane, number>>
  board_from_turn: string
  elapsed_ms: number
  warnings: string[]
}

export interface NiraAttachment {
  id: string
  name: string
  mime: string
  kind: string
  summary: string
  facts: string
  issues: NiraIssue[]
  names_to_redact: string[]
  error: string
  created_at: string
}

export interface NiraMessage {
  id: string
  role: "user" | "assistant"
  text: string
  attachments: NiraAttachment[]
  turn: NiraTurn | null
  status: "pending" | "complete" | "failed"
  error: string
  created_at: string
  ui_mode?: string | null
  ui_response?: Record<string, unknown> | null
}

export interface NiraThreadSummary {
  id: string
  title: string
  context: NiraContext
  created_at: string
  updated_at: string
  turns: number
  last_question: string
  authorities: number
  annotations: NiraAnnotations
  running: boolean
}

export interface NiraThread extends NiraThreadSummary {
  messages: NiraMessage[]
}

export interface NiraPassageRead {
  doc_id: string
  paragraphs: Array<{ chunk_id: string; para: string; role: string; text: string }>
  total_paragraphs: number | null
  truncated: boolean
  next_offset: number | null
  notes: string[]
}

export const LANE_LABEL: Record<NiraLane, string> = {
  for: "Holds for you",
  against: "Used against you",
  dont_cite: "Don't cite",
  check: "Check before relying",
}

export const LANE_SHORT: Record<NiraLane, string> = {
  for: "For you",
  against: "Against you",
  dont_cite: "Don't cite",
  check: "Check first",
}

export const LANE_ORDER: NiraLane[] = ["for", "against", "check", "dont_cite"]

export const ACTION_LABEL: Record<NiraAction, string> = {
  follow: "Follow",
  distinguish: "Distinguish",
  not_follow: "Don't follow",
  cite_with_care: "Cite with care",
}

export const SIDE_LABEL: Record<NiraSide, string> = {
  revenue: "For Revenue",
  taxpayer: "For the taxpayer",
  mixed: "Mixed result",
  neutral: "Neutral",
  unclear: "Result unclear",
  none: "No result on merits",
}

export const DEFAULT_CONTEXT: NiraContext = {
  state_code: "36",
  administration: "central",
  as_of: null,
  side: "revenue",
  forums: [],
  kinds: [],
  include_pre_gst: true,
}

export const EMPTY_ANNOTATIONS: NiraAnnotations = { order: [], overrides: {}, dismissed: [] }

/** GST State codes, as the backend's `nira/states.py`. */
export const STATES: Array<[string, string]> = [
  ["01", "Jammu and Kashmir"], ["02", "Himachal Pradesh"], ["03", "Punjab"], ["04", "Chandigarh"],
  ["05", "Uttarakhand"], ["06", "Haryana"], ["07", "Delhi"], ["08", "Rajasthan"], ["09", "Uttar Pradesh"],
  ["10", "Bihar"], ["11", "Sikkim"], ["12", "Arunachal Pradesh"], ["13", "Nagaland"], ["14", "Manipur"],
  ["15", "Mizoram"], ["16", "Tripura"], ["17", "Meghalaya"], ["18", "Assam"], ["19", "West Bengal"],
  ["20", "Jharkhand"], ["21", "Odisha"], ["22", "Chhattisgarh"], ["23", "Madhya Pradesh"], ["24", "Gujarat"],
  ["26", "Dadra and Nagar Haveli and Daman and Diu"], ["27", "Maharashtra"], ["29", "Karnataka"], ["30", "Goa"],
  ["31", "Lakshadweep"], ["32", "Kerala"], ["33", "Tamil Nadu"], ["34", "Puducherry"],
  ["35", "Andaman and Nicobar Islands"], ["36", "Telangana"], ["37", "Andhra Pradesh"], ["38", "Ladakh"],
]

/** The High Court that binds a CGST officer posted in each State (territorial
 * jurisdiction over the commissionerate). Names as Nira's index gives them. */
export const HIGH_COURT_BY_STATE: Record<string, string> = {
  "01": "High Court of Jammu and Kashmir and Ladakh", "02": "High Court of Himachal Pradesh",
  "03": "High Court of Punjab and Haryana", "04": "High Court of Punjab and Haryana", "05": "High Court of Uttarakhand",
  "06": "High Court of Punjab and Haryana", "07": "High Court of Delhi", "08": "High Court of Rajasthan",
  "09": "Allahabad High Court", "10": "Patna High Court", "11": "High Court of Sikkim", "12": "Gauhati High Court",
  "13": "Gauhati High Court", "14": "High Court of Manipur", "15": "Gauhati High Court", "16": "High Court of Tripura",
  "17": "High Court of Meghalaya", "18": "Gauhati High Court", "19": "Calcutta High Court", "20": "High Court of Jharkhand",
  "21": "Orissa High Court", "22": "High Court of Chhattisgarh", "23": "High Court of Madhya Pradesh",
  "24": "High Court of Gujarat", "26": "Bombay High Court", "27": "Bombay High Court", "29": "High Court of Karnataka",
  "30": "Bombay High Court", "31": "High Court of Kerala", "32": "High Court of Kerala", "33": "Madras High Court",
  "34": "Madras High Court", "35": "Calcutta High Court", "36": "High Court for the State of Telangana",
  "37": "High Court of Andhra Pradesh", "38": "High Court of Jammu and Kashmir and Ladakh",
}

/**
 * Where a CGST officer can research from today: every State and UT whose
 * High Court has GST decisions in Nira's index (measured 29 Sep 2026 — every
 * one but Manipur, which returned none). Nira indexes GST judgments; CESTAT
 * (pre-GST) partly; GSTAT not yet.
 */
export const CGST_JURISDICTIONS: Array<[string, string]> = STATES.filter(([code]) => code !== "14")

export function stateName(code: string): string {
  return STATES.find(([c]) => c === code)?.[1] ?? code
}
